import { type ChordDefinition, normalizeChordSymbol } from "@bandwith/chords";
import { type AudioSynth, createAudioSynth } from "@bandwith/platform";
import { Interval, Note } from "tonal";

// Re-export the type for convenience
export type { ChordDefinition } from "@bandwith/chords";

/**
 * Standard guitar tuning from low E to high E (strings 6 to 1)
 * Each note includes its octave for accurate frequency calculation
 */
const GUITAR_TUNING = ["E2", "A2", "D3", "G3", "B3", "E4"];

/**
 * Lazy-initialized synth singleton.
 * Created on first use to avoid issues with SSR.
 */
let synth: AudioSynth | null = null;

function getSynth(): AudioSynth {
  if (!synth) {
    synth = createAudioSynth();
  }
  return synth;
}

interface PlayChordOptions {
  /** Base octave for the chord (default: 3) */
  octave?: number;
  /** Duration in seconds (default: 2) */
  duration?: number;
  /** Volume from 0 to 1 (default: 0.3) */
  volume?: number;
  /** Strum delay between notes in seconds (default: 0.03) */
  strumDelay?: number;
}

/**
 * Calculate the actual notes from a chord definition's fret positions.
 * Uses standard guitar tuning and transposes each open string by the fret number.
 *
 * @param definition - The chord definition with fret positions
 * @returns Array of note names with octaves (e.g., ["E2", "B2", "E3", "G#3", "B3", "E4"])
 */
export function getNotesFromFrets(definition: ChordDefinition): string[] {
  const notes: string[] = [];

  for (let i = 0; i < definition.frets.length; i++) {
    const fret = definition.frets[i];

    // Skip muted strings and undefined frets
    if (fret === "x" || fret === undefined) continue;

    const openNote = GUITAR_TUNING[i];
    if (!openNote) continue;

    // Transpose the open string note by the number of semitones (frets)
    // baseFret shifts all fret positions up (e.g., baseFret 3 means fret "1" is actually fret 3)
    const actualFret = fret === 0 ? 0 : fret + definition.baseFret - 1;
    const interval = Interval.fromSemitones(actualFret);
    const note = Note.transpose(openNote, interval);

    if (note) {
      notes.push(note);
    }
  }

  return notes;
}

/**
 * Play a chord by its symbol (e.g., "Am", "G7", "Cmaj7")
 * Returns true if the chord was played, false if it couldn't be parsed
 */
export async function playChord(
  chordSymbol: string,
  options: PlayChordOptions = {},
): Promise<boolean> {
  const normalized = normalizeChordSymbol(chordSymbol);

  try {
    await getSynth().playChord(normalized, options);
    return true;
  } catch (e) {
    console.warn(`Could not play chord: ${chordSymbol}`, e);
    return false;
  }
}

/**
 * Play a chord from its fret definition (for custom chords like "Gchill")
 * Uses the actual fret positions to determine the notes to play.
 */
export async function playChordFromDefinition(
  definition: ChordDefinition,
  options: Omit<PlayChordOptions, "octave"> = {},
): Promise<boolean> {
  const notes = getNotesFromFrets(definition);
  if (notes.length === 0) {
    console.warn(`No playable notes in chord: ${definition.name}`);
    return false;
  }

  // Convert relative frets to absolute fret positions for the synth
  const frets: (number | null)[] = definition.frets.map((fret) => {
    if (fret === "x" || fret === undefined) return null;
    return fret === 0 ? 0 : fret + definition.baseFret - 1;
  });

  try {
    await getSynth().playChordFromFrets(frets, options);
    return true;
  } catch (e) {
    console.warn(`Could not play chord from definition: ${definition.name}`, e);
    return false;
  }
}

/**
 * Play a single string at its current fret position.
 * Used for strumming effect where each string plays as finger crosses it.
 */
export async function playStringNote(
  stringIndex: number,
  fret: number | "x",
  options: Omit<PlayChordOptions, "octave"> = {},
): Promise<boolean> {
  if (fret === "x") return false;

  try {
    await getSynth().playStringNote(stringIndex, fret, options);
    return true;
  } catch (e) {
    console.warn(
      `Could not play string note: string ${stringIndex}, fret ${fret}`,
      e,
    );
    return false;
  }
}
